import { useState } from "react";
import {
  AcademicCapIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  AdjustmentsHorizontalIcon,
  ClockIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  FlagIcon,
  LinkIcon,
  PencilSquareIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, Tag } from "../ui";
import SetupDialog from "../calls/SetupDialog";
import { READ_ONLY_NOTE, canManageScholarships } from "./access";
import { applicationLinkOf, stateOf, useScholarshipChange, useStaffScholarship } from "./model";
import { saveDrafts } from "./saveDrafts";
import ScholarshipPreview from "./ScholarshipPreview";
import DetailsTab from "./DetailsTab";
import DocumentsTab from "./DocumentsTab";
import EligibilityTab from "./EligibilityTab";
import EvaluatorsTab from "./EvaluatorsTab";
import HistoryTab from "./HistoryTab";
import ProgrammesTab from "./ProgrammesTab";
import CriteriaTab from "./CriteriaTab";
import StatusTab from "./StatusTab";
import InvitationsTab from "./InvitationsTab";

const TABS = [
  { value: "details", label: "Details", icon: <PencilSquareIcon className="h-4 w-4" /> },
  // Documents belong to programmes, so programmes come first
  { value: "programmes", label: "Programmes", icon: <AcademicCapIcon className="h-4 w-4" /> },
  { value: "documents", label: "Documents", icon: <DocumentTextIcon className="h-4 w-4" /> },
  { value: "eligibility", label: "Eligibility", icon: <UserGroupIcon className="h-4 w-4" /> },
  { value: "criteria", label: "Criteria", icon: <AdjustmentsHorizontalIcon className="h-4 w-4" /> },
  { value: "evaluators", label: "Scholarship Review Committee", icon: <UsersIcon className="h-4 w-4" /> },
  { value: "status", label: "Status", icon: <FlagIcon className="h-4 w-4" /> },
  { value: "history", label: "History", icon: <ClockIcon className="h-4 w-4" /> },
];
// A private scholarship also lists the students it is for, before its status
const INVITATIONS = { value: "invitations", label: "Invitations", icon: <EnvelopeIcon className="h-4 w-4" /> };
const PRIVATE_TABS = TABS.flatMap((t) => (t.value === "status" ? [INVITATIONS, t] : [t]));

const PANELS = {
  details: DetailsTab,
  programmes: ProgrammesTab,
  documents: DocumentsTab,
  eligibility: EligibilityTab,
  criteria: CriteriaTab,
  evaluators: EvaluatorsTab,
  status: StatusTab,
  history: HistoryTab,
  invitations: InvitationsTab,
};

// Private scholarships are in no list; invited students also get this link by email
const copyLink = async (scholarship) => {
  try {
    await navigator.clipboard.writeText(applicationLinkOf(scholarship.id));
    toast.success("Link copied. Send it to the invited students.");
  } catch (error) {
    console.error("Failed to copy the application link:", error);
    toast.error(`Could not copy. The link is ${applicationLinkOf(scholarship.id)}`);
  }
};

const listOf = (labels) => labels.join(", ");

// What "Save draft" did, as one toast
const reportSaved = ({ saved, skipped, failed }) => {
  if (!saved.length && !skipped.length && !failed.length) return toast.success("Nothing to save: every tab is up to date.");
  if (saved.length) toast.success(`Saved: ${listOf(saved)}.`);
  if (skipped.length) toast.error(`Not saved, fix them on their tab first: ${listOf(skipped)}.`);
  if (failed.length) toast.error(`Could not save: ${listOf(failed)}. Open the tab to see why.`);
};

/**
 * A scholarship's whole set-up (journey steps 1-4) in one dialog, one tab per part. While a draft,
 * "Save draft" saves every tab's unsaved changes at once and "Preview" shows the whole set-up and the student page.
 */
export default function ScholarshipSetupDialog({ scholarshipId, initialTab = "details", onClose }) {
  const { data, isPending, isError, refetch } = useStaffScholarship(scholarshipId);
  const scholarship = data?.data;
  const state = stateOf(scholarship?.state);
  const change = useScholarshipChange(scholarshipId);
  const [savingDraft, setSavingDraft] = useState(false);
  const [saveRound, setSaveRound] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  const isDraft = scholarship?.state === "DRAFT";

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      reportSaved(await saveDrafts(scholarship, change));
    } finally {
      setSavingDraft(false);
      // The open tab reads its drafts again
      setSaveRound((n) => n + 1);
    }
  };

  return (
    <SetupDialog
      open={Boolean(scholarshipId)}
      onClose={onClose}
      title={scholarship?.name}
      tag={scholarship && <Tag color={state.color}>{state.label}</Tag>}
      tabs={scholarship?.visibility === "PRIVATE" ? PRIVATE_TABS : TABS}
      panels={PANELS}
      panelProps={{ scholarship }}
      initialTab={initialTab}
      isPending={isPending}
      isError={isError}
      onRefresh={refetch}
      readOnlyNote={READ_ONLY_NOTE}
      panelKey={saveRound}
      actions={
        <>
          {scholarship?.visibility === "PRIVATE" && scholarship.state === "PUBLISHED" && (
            <Button variant="outline" icon={<LinkIcon className="h-4 w-4" />} onClick={() => copyLink(scholarship)}>
              Copy application link
            </Button>
          )}
          {isDraft && (
            <Button variant="outline" icon={<EyeIcon className="h-4 w-4" />} onClick={() => setPreviewing(true)}>
              Preview
            </Button>
          )}
          {isDraft && canManageScholarships() && (
            <Button icon={<ArrowDownTrayIcon className="h-4 w-4" />} onClick={saveDraft} loading={savingDraft}>
              Save draft
            </Button>
          )}
          {previewing && scholarship && <ScholarshipPreview scholarship={scholarship} onClose={() => setPreviewing(false)} />}
        </>
      }
    />
  );
}
